import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Filter, LayoutTemplate, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePermission } from '@/lib/permissions';
import { toast } from 'sonner';
import {
  runExport,
  useCreateTemplate,
  useExportTemplates,
  useUpdateTemplate,
  EXPORT_STATUS_OPTIONS,
  type ExportFieldGroup,
  type ExportFilters,
  type ExportFormat,
  type ExportStatus,
  type ExportTemplate,
  type FieldEntry,
} from '@/lib/export';
import { FieldCatalog } from './field-catalog';
import { SelectedColumns } from './selected-columns';
import { TemplatePicker } from './template-picker';

/** A new template starts pre-filled with these columns (only the ones the user may see). */
const DEFAULT_PRESET = [
  'profile.fullName',
  'profile.workEmail',
  'org.department',
  'org.position',
  'org.manager',
  'org.startDate',
  'org.status',
];

interface TemplateBuilderProps {
  template?: ExportTemplate;
  groups: ExportFieldGroup[];
  onDone: () => void;
}

export function TemplateBuilder({ template, groups, onDone }: TemplateBuilderProps) {
  const isEdit = Boolean(template);
  const canShare = usePermission('export.templates.share');
  const canDownload = usePermission('export.download');

  // Export is Excel-only for now (PDF support is paused).
  const format: ExportFormat = 'xlsx';
  const [name, setName] = useState(template?.name ?? '');
  const [shared, setShared] = useState(template?.scope === 'shared');
  // New templates open with a sensible default preset (intersected with visible fields).
  const presetSelected = useMemo<FieldEntry[]>(() => {
    const visibleIds = new Set(groups.flatMap((g) => g.fields.map((f) => f.id)));
    return DEFAULT_PRESET.filter((id) => visibleIds.has(id)).map((id) => ({ fieldId: id }));
  }, [groups]);
  const [selected, setSelected] = useState<FieldEntry[]>(template?.fields ?? presetSelected);
  const [statusFilter, setStatusFilter] = useState<ExportStatus[]>(template?.filters?.status ?? []);
  const [exporting, setExporting] = useState(false);

  const filters: ExportFilters | undefined = statusFilter.length ? { status: statusFilter } : undefined;
  const toggleStatus = (s: ExportStatus) =>
    setStatusFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const createMut = useCreateTemplate();
  const updateMut = useUpdateTemplate();
  const saving = createMut.isPending || updateMut.isPending;

  // Load an existing template's columns as a starting point.
  const templatesQuery = useExportTemplates();
  const loadable = (templatesQuery.data ?? []).filter((t) => t.id !== template?.id);
  const loadFrom = (t: ExportTemplate) => {
    if (!name.trim()) setName(t.name);
    setShared(t.scope === 'shared');
    setSelected(t.fields);
    setStatusFilter(t.filters?.status ?? []);
    toast.message(`Loaded “${t.name}”`);
  };

  // Flat id → label map for headers/placeholders. Only fields the user may see appear.
  const labelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) for (const f of g.fields) m.set(f.id, f.label);
    return m;
  }, [groups]);
  const labelFor = (id: string) => labelMap.get(id) ?? id;

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.fieldId)), [selected]);

  const toggle = (fieldId: string) =>
    setSelected((prev) =>
      prev.some((s) => s.fieldId === fieldId)
        ? prev.filter((s) => s.fieldId !== fieldId)
        : [...prev, { fieldId }],
    );

  const rename = (fieldId: string, header: string | undefined) =>
    setSelected((prev) => prev.map((s) => (s.fieldId === fieldId ? { ...s, header } : s)));

  const remove = (fieldId: string) => setSelected((prev) => prev.filter((s) => s.fieldId !== fieldId));

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Give your template a name');
    if (selected.length === 0) return toast.error('Pick at least one column');
    const input = {
      name: name.trim(),
      scope: (shared && canShare ? 'shared' : 'personal') as ExportTemplate['scope'],
      format,
      fields: selected,
      filters,
    };
    try {
      if (template) await updateMut.mutateAsync({ id: template.id, input });
      else await createMut.mutateAsync(input);
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onDone();
    } catch {
      toast.error('Could not save the template');
    }
  };

  const handleExportNow = async () => {
    if (selected.length === 0) return toast.error('Pick at least one column');
    setExporting(true);
    try {
      await runExport({ fields: selected, format, filters });
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={onDone} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Untitled export"
          className="h-9 w-56 font-medium"
        />
        <TooltipProvider delayDuration={300}>
        <div className="ml-auto flex items-center gap-1.5">
          {canShare && (
            <div className="mr-1 flex items-center gap-2">
              <Label
                htmlFor="share-template"
                className="cursor-pointer whitespace-nowrap text-sm text-muted-foreground"
              >
                Share with team
              </Label>
              <Switch id="share-template" checked={shared} onCheckedChange={setShared} />
            </div>
          )}

          {loadable.length > 0 && (
            <TemplatePicker
              templates={loadable}
              onPick={loadFrom}
              align="end"
              tooltip="Load template"
              contentLabel="Load template"
              trigger={
                <Button variant="ghost" size="icon" className="rounded-full" aria-label="Load template">
                  <LayoutTemplate className="h-4 w-4" />
                </Button>
              }
            />
          )}

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn('relative rounded-full', statusFilter.length > 0 && 'text-primary')}
                    aria-label="Filter rows by status"
                  >
                    <Filter className="h-4 w-4" />
                    {statusFilter.length > 0 && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {statusFilter.length ? `Filters · ${statusFilter.length} selected` : 'Filter rows by status'}
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Employee status</DropdownMenuLabel>
              {EXPORT_STATUS_OPTIONS.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={statusFilter.includes(opt.value)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={() => toggleStatus(opt.value)}
                  className="cursor-pointer"
                >
                  {opt.label}
                </DropdownMenuCheckboxItem>
              ))}
              {statusFilter.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setStatusFilter([])}>Clear</DropdownMenuItem>
                </>
              ) : (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  Default: active roster (excludes terminated).
                </p>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="mx-1 h-5 w-px bg-border" aria-hidden="true" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={handleExportNow}
                disabled={exporting || selected.length === 0 || !canDownload}
                aria-label="Export now"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {canDownload ? 'Export now (Excel)' : 'You lack the “Download data exports” permission'}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={handleSave}
                disabled={saving}
                aria-label={isEdit ? 'Save changes' : 'Save template'}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isEdit ? 'Save changes' : 'Save template'}</TooltipContent>
          </Tooltip>
        </div>
        </TooltipProvider>
      </div>

      {/* Split: catalog | selected columns */}
      <div className="grid flex-1 grid-cols-1 gap-6 overflow-hidden p-6 md:grid-cols-2">
        <section className="flex min-h-0 flex-col">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Available fields</h3>
          <FieldCatalog groups={groups} selectedIds={selectedIds} onToggle={toggle} />
        </section>
        <section className="flex min-h-0 flex-col">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Columns <span className="text-muted-foreground">({selected.length})</span>
          </h3>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <SelectedColumns
              selected={selected}
              labelFor={labelFor}
              onReorder={setSelected}
              onRename={rename}
              onRemove={remove}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
